import {
  Button,
  theme as defaultTheme,
  Icon,
  useSnackbar,
} from "@mindlercare/ui-web";
import { isForbiddenError } from "api/helpers";
import {
  useQuestionnaireTableData,
  useUpdateUserAnswersMutation,
} from "api/useQuestionnaireQueries";
import { OpenSingleQuestionnaire } from "components/graphs/DashboardLinearGraph";
import BackButton from "components/lib/backButton";
import PageHeader from "components/lib/pageHeader";
import { SendQuestionnaireModal } from "components/questionnaires/questionnaireModal/newQuestionnaireModal";
import { ViewQuestionnaireModal } from "components/questionnaires/questionnaireModal/viewQuestionnaireModal";
import { QuestionnairesDashboard } from "components/questionnaires/questionnairesDashboard";
import QuestionnaireTable from "components/questionnaires/questionnairesTable";
import { Typography } from "components/typography";
import { UserAnswer } from "core/types";
import { useUserHasRole } from "hooks/userHooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { styled } from "styled-components";

import { useMindlerApi } from "api/apiv2";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useCurrentMarketCode } from "../core/selectors/marketSelectors";
import { useButtonEvent } from "../hooks/eventhooks";

const Container = styled.div`
  padding: 32px;
  min-height: 100px;

  th:nth-child(1) {
    width: 28%;
  }
  td:nth-child(1) {
    width: 40%;
  }
  td:nth-child(3) {
    width: 10%;
  }
  td:nth-child(4) {
    width: 15%;
  }
`;

const StyledLink = styled.a`
  color: ${({ theme }) => defaultTheme.COLORS.GRAY_BLACK};
`;

const TopBarContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const ButtonWrapper = styled.div`
  padding: 0 20px;

  .iconButton {
    padding: 0 20px;
  }
`;

const getUrl = (market: string) => {
  switch (market) {
    case "SE": {
      return "https://docs.google.com/document/d/1-LUmz3P5gr2PW-QmS27ODE2jSyDntyDo/edit";
    }
    case "UK":
    case "GB": {
      return "https://drive.google.com/drive/folders/13kZArB688gubhaJO6XoDHWde8qvKfE03?usp=sharing";
    }
    default: {
      return "";
    }
  }
};
const isUserAnswer = (item: UserAnswer | undefined): item is UserAnswer => {
  return !!item;
};

const Questionnaires = () => {
  const { enqueueSnackbar } = useSnackbar();
  const buttonEventDispatcher = useButtonEvent({
    pressableId: "questionnaire-request", // :poika:
  });
  const currentMarket = useCurrentMarketCode();
  const dispatchQuestionnaireInteracted = (clinicalId: string) => {
    buttonEventDispatcher(`questionnaire-request-${clinicalId}`);
  };

  const location: {
    state: { clinicalId: string; requestId: string };
  } = useLocation();

  const { userId: patientUserId } = useParams<{ userId: string }>();
  const { t } = useTranslation();
  const isAdminOrPsychologist = useUserHasRole(["Admin", "Psychologist"]);

  const [newModalIsOpen, setNewModalIsOpen] = useState<boolean>(false);
  const [viewQuestionnaireIsOpen, setViewQuestionnaireIsOpen] =
    useState<boolean>(false);
  const [selectedClinicalId, setSelectedClinicalId] = useState<
    string | undefined
  >();
  const [selectedRequestId, setSelectedRequestId] = useState<
    string | undefined
  >();
  const [showNotification, setShowNotification] = useState(false);

  const apiv2 = useMindlerApi();
  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(patientUserId),
      },
      {
        enabled: !!patientUserId,
      },
    ),
  );
  const { mutate: updateUserAnswers } = useUpdateUserAnswersMutation();

  const {
    data: patientQuestionnaireAnswersAndRequests,
    isLoading,
    errors,
    isError,
  } = useQuestionnaireTableData({
    patientUserId,
  });

  useEffect(() => {
    if (isError && Object.values(errors).every((e) => !isForbiddenError(e))) {
      enqueueSnackbar({
        text: t("SOMETHING_WENT_WRONG"),
        isError: true,
      });
    }
  }, [isError, enqueueSnackbar, errors, t]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (showNotification) {
      timer = setTimeout(() => {
        setShowNotification(false);
      }, 2000);
    }
    return () => timer && clearTimeout(timer);
  }, [showNotification, setShowNotification]);

  useEffect(() => {
    if (location?.state?.clinicalId && location?.state?.requestId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedClinicalId(location?.state?.clinicalId);

      setSelectedRequestId(location?.state?.requestId);

      setViewQuestionnaireIsOpen(true);
    }
  }, [location?.state?.clinicalId, location?.state?.requestId]);

  const howDoIUseThisUrl = getUrl(currentMarket);

  return (
    <>
      {isAdminOrPsychologist && (
        <Container>
          {patient && (
            <>
              <TopBarContainer>
                <BackButton />
                <ButtonWrapper>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setNewModalIsOpen(true);
                    }}
                    testId="newQuestionnaireButton"
                    className="iconButton"
                  >
                    <Icon.Add />
                    {t(
                      "PATIENT_QUESTIONNAIRE_TABLE_NEW_QUESTIONNAIRE_BUTTON_TEXT",
                    )}
                  </Button>
                </ButtonWrapper>
              </TopBarContainer>
              <PageHeader
                hasSensitive={true}
                translation={t("PAGE_HEADER_QUESTIONNAIRES", {
                  patient: `${patient?.firstName}`,
                })}
              />
              <Typography insensitive variant="body" color="GRAY_DARK">
                {t("PATIENT_QUESTIONNAIRE_TABLE_DESCRIPTION")}{" "}
                {Boolean(howDoIUseThisUrl) && (
                  <StyledLink href={getUrl(currentMarket)}>
                    {t("PATIENT_QUESTIONNAIRE_TABLE_HOW_TO_LINK")}
                  </StyledLink>
                )}
              </Typography>
            </>
          )}
          <QuestionnairesDashboard
            patientUserId={patientUserId}
            openSingleQuestionnaire={({
              clinicalId,
              requestId,
              isNew,
            }: OpenSingleQuestionnaire) => {
              if (!clinicalId) return;
              setSelectedClinicalId(clinicalId);
              setSelectedRequestId(requestId);
              setViewQuestionnaireIsOpen(true);
              dispatchQuestionnaireInteracted(clinicalId);
              if (isNew && patientUserId) {
                updateUserAnswers({
                  patientUserId,
                  payload: { id: requestId, psychologistSeenAt: new Date() },
                });
              }
            }}
          />
          <QuestionnaireTable
            patientUserId={patientUserId}
            patientName={`${patient?.firstName} ${patient?.lastName}`}
            rows={
              isLoading && !patientQuestionnaireAnswersAndRequests
                ? []
                : patientQuestionnaireAnswersAndRequests
            }
            onClick={({ clinicalId, id, userAnswer, isNew }) => {
              if (!clinicalId) return;
              setSelectedClinicalId(clinicalId);
              setSelectedRequestId(id);
              setViewQuestionnaireIsOpen(true);
              dispatchQuestionnaireInteracted(clinicalId);
              if (isNew && userAnswer && patientUserId) {
                updateUserAnswers({
                  patientUserId,
                  payload: { id, psychologistSeenAt: new Date() },
                });
              }
            }}
          />
          <SendQuestionnaireModal
            patientName={`${patient?.firstName} ${patient?.lastName}`}
            setModalIsOpen={setNewModalIsOpen}
            patientUserId={Number(patientUserId)}
            open={newModalIsOpen}
          />
          <ViewQuestionnaireModal
            clinicalId={selectedClinicalId}
            requestId={selectedRequestId}
            setModalIsOpen={setViewQuestionnaireIsOpen}
            open={viewQuestionnaireIsOpen}
            patientName={`${patient?.firstName} ${patient?.lastName}`}
            patientUserId={Number(patientUserId)}
            answers={patientQuestionnaireAnswersAndRequests
              .find((qa) => qa.id === selectedRequestId)
              ?.userAnswer?.filter(isUserAnswer)}
          />
        </Container>
      )}
    </>
  );
};

export default Questionnaires;
